import React, { useEffect, useState } from "react";

export type AlertType = "success" | "warning" | "error" | "info";

interface AlertMessageProps {
  message?: string;
  type?: AlertType;
  duration?: number; // Optional: duration in ms
  onClose?: () => void; // Optional: callback on dismiss
}

const AlertMessage: React.FC<AlertMessageProps> = ({
  message = "Something happened!",
  type = "info",
  duration = 3000,
  onClose,
}) => {
  const [visible, setVisible] = useState(true);

 useEffect(() => {
  if (!message) return;

  setVisible(true); // force re-show on every new message

  const timeout = setTimeout(() => {
    setVisible(false);
    onClose?.();
  }, duration);

  return () => clearTimeout(timeout);
}, [message, duration, onClose]);


  if (!visible) return null;

  const backgroundColors: Record<AlertType, string> = {
    success: "#d4edda",
    warning: "#fff3cd",
    error: "#f8d7da",
    info: "#cce5ff",
  };

  const textColors: Record<AlertType, string> = {
    success: "#155724",
    warning: "#856404",
    error: "#721c24",
    info: "#004085",
  };

  const icon: Record<AlertType, string> = {
    success: "✔️",
    warning: "⚠️",
    error: "❌",
    info: "ℹ️",
  };

  return (
    <>
      <style>
        {`
          @keyframes fadeSlide {
            0% {
              opacity: 0;
              transform: translateY(-10px);
            }
            100% {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>

      <div
        style={{
          backgroundColor: backgroundColors[type],
          color: textColors[type],
          borderRadius: "0.5rem",
          padding: "1rem 1.5rem",
          display: "flex",
          alignItems: "center",
          gap: "0.75rem",
          fontFamily: "Segoe UI, Tahoma, Geneva, Verdana, sans-serif",
          fontSize: "1rem",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          animation: "fadeSlide 0.4s ease-out",
          margin: "1rem auto",
          maxWidth: "600px",
          transition: "opacity 0.3s ease",
          opacity: visible ? 1 : 0,
        }}
      >
        <span style={{ fontSize: "1.5rem" }}>{icon[type]}</span>
        <span>{message}</span>
      </div>
    </>
  );
};

export default AlertMessage;
